import { Trophy, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  GameStatus,
  useBingoGameStore,
} from "@/plugins/bingo/state/bingoGameStore";

const STAGE_LABELS = {
  one_line: "1 LINE",
  two_lines: "2 LINES",
  full_house: "FULL HOUSE",
};

/**
 * WinnerPanel: text field + Announce Winner button.
 * On announce, publishes an event and sets status=ANNOUNCING_WINNER,
 * which triggers the celebration overlay on the Projector (Step 9).
 */
export default function WinnerPanel() {
  const stage = useBingoGameStore((s) => s.stage);
  const winners = useBingoGameStore((s) => s.winners);
  const pending = useBingoGameStore((s) => s.pendingWinnerName);
  const setPending = useBingoGameStore((s) => s.setPendingWinnerName);
  const announce = useBingoGameStore((s) => s.announceWinner);
  const dismiss = useBingoGameStore((s) => s.dismissWinner);
  const status = useBingoGameStore((s) => s.status);
  const isAnnouncing = status === GameStatus.ANNOUNCING_WINNER;

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ic-text-muted)]">
        Winner — {STAGE_LABELS[stage]}
      </div>
      <Input
        data-testid="host-winner-input"
        placeholder="Winner name or ticket #"
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !isAnnouncing) announce();
        }}
        className="bg-[color:var(--ic-bg-elevated)] border-[color:var(--ic-border-subtle)]"
      />
      {!isAnnouncing ? (
        <button
          data-testid="host-announce-winner"
          type="button"
          onClick={announce}
          disabled={status === GameStatus.IDLE}
          className="h-10 rounded-md font-semibold text-black transition-all disabled:opacity-40 hover:brightness-110 active:scale-[0.99] inline-flex items-center justify-center gap-2"
          style={{
            background: "var(--ic-primary)",
            boxShadow: "0 0 20px var(--ic-primary-glow)",
          }}
        >
          <Trophy className="w-4 h-4" strokeWidth={2.5} />
          Announce Winner
        </button>
      ) : (
        <button
          data-testid="host-dismiss-winner"
          type="button"
          onClick={dismiss}
          className="h-10 rounded-md text-sm border border-[color:var(--ic-border-subtle)] text-white hover:border-[color:var(--ic-border-focus)] inline-flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          Dismiss Overlay
        </button>
      )}

      {/* Recap of recorded winners */}
      <div className="mt-2 rounded-md border border-[color:var(--ic-border-subtle)] bg-[color:var(--ic-bg-elevated)] divide-y divide-[color:var(--ic-border-subtle)]">
        {Object.entries(STAGE_LABELS).map(([key, label]) => (
          <div
            key={key}
            data-testid={`host-winner-record-${key}`}
            className="flex items-center justify-between px-3 py-2 text-xs"
          >
            <span className="text-[color:var(--ic-text-muted)] tracking-widest uppercase">
              {label}
            </span>
            <span className="text-white truncate max-w-[60%] text-right">
              {winners[key]?.name || "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
